import { useMemo, useState } from 'react';
import { Empty, Modal, Select, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import { ordersApi } from '../../../api/orders';
import { StatusChip, type ChipTone } from '../../../components/ui/StatusChip';
import { formatDate, formatQty } from '../../../utils/formatters';
import { averageProgress, ordersInStatus, sortByDeadline } from '../ordersProgress';
import type { OrdersOverview, OrderOverviewRow } from '../../../types/dashboard';

/**
 * Виконання замовлень: одне коло замість списку.
 *
 * Три режими. «Усі замовлення» — коло поділене за станом, у центрі кількість
 * замовлень, праворуч список усіх активних. Клік по стану (легенда або сегмент)
 * лишає його в кольорі, решту притлумлює, у центрі — середній відсоток
 * виконання саме цих замовлень, праворуч — лише вони. Обране в списку
 * замовлення — кільце «виготовлено з потрібного» і його позиції.
 *
 * Статуси — порядкова шкала (воронка), тому барва одна, від світлої до
 * темної: так «наскільки далеко просунулось» читається з кольору, а пари
 * сусідів не зливаються для дальтоніків. Кроки перевірені валідатором
 * (монотонна яскравість, проміжки ≥ 0.06, світлий край 2.74:1 на білому).
 *
 * Набір станів задає бекенд (`by_status`) — тут нічого не зашито: статус без
 * свого кроку барви отримує нейтральний колір, а не ламає коло.
 */
const STATUS_RAMP: Record<string, string> = {
  new: '#ce8d6f',
  in_work: '#b6653f',
  ready: '#9a410c',
  partial: '#732600',
};

/** Тон пігулки — той самий, що на сторінці замовлень. */
const STATUS_TONE: Record<string, ChipTone> = {
  new: 'info',
  in_work: 'info',
  ready: 'success',
  partial: 'warning',
  shipped: 'success',
  cancelled: 'neutral',
};

const ALL = 0;
const R = 72;
const STROKE = 16;
const C = 2 * Math.PI * R;
/** Зазор між сегментами — «поверхня робить розділення», а не обвідка. */
const GAP = 2;

function rampColor(status: string): string {
  return STATUS_RAMP[status] ?? 'var(--text-hairline)';
}

function deadlineLabel(days: number | null): { cls: string; text: string } {
  if (days === null) return { cls: 'ok', text: 'без дедлайну' };
  if (days < 0) return { cls: 'urgent', text: `прострочено ${Math.abs(days)} дн.` };
  if (days === 0) return { cls: 'urgent', text: 'сьогодні' };
  if (days < 14) return { cls: 'urgent', text: `через ${days} дн.` };
  if (days < 30) return { cls: 'warn', text: `через ${days} дн.` };
  return { cls: 'ok', text: `через ${days} дн.` };
}

function orderNumber(o: OrderOverviewRow): string {
  return o.external_number ? `№ ${o.external_number}` : o.code;
}

function orderLabel(o: OrderOverviewRow): string {
  return `${orderNumber(o)} · ${o.customer_name}`;
}

/* ── Коло за статусами ─────────────────────────────────────────────────── */

function StatusDonut({
  overview,
  hovered,
  selectedStatus,
  centerValue,
  centerCaption,
  onHover,
  onSelect,
}: {
  overview: OrdersOverview;
  hovered: string | null;
  selectedStatus: string | null;
  centerValue: string;
  centerCaption: string;
  onHover: (status: string | null) => void;
  onSelect: (status: string) => void;
}) {
  const slices = overview.by_status.filter((s) => s.count > 0);
  const total = slices.reduce((a, s) => a + s.count, 0);
  const gap = slices.length > 1 ? GAP : 0;

  let offset = 0;
  const arcs = slices.map((s) => {
    const len = (s.count / total) * C;
    const dash = Math.max(0, len - gap);
    const arc = { ...s, dash, offset };
    offset += len;
    return arc;
  });

  /** Притлумлення спільне для наведення і для обраного стану. */
  const active = hovered ?? selectedStatus;

  return (
    <svg viewBox="0 0 180 180" className="dash-oprog-ring" role="group" aria-label="Стан замовлень">
      <g transform="rotate(-90 90 90)">
        {arcs.map((a) => (
          <circle
            key={a.status}
            cx="90"
            cy="90"
            r={R}
            fill="none"
            stroke={rampColor(a.status)}
            strokeWidth={STROKE}
            strokeDasharray={`${a.dash} ${C - a.dash}`}
            strokeDashoffset={-a.offset}
            opacity={active && active !== a.status ? 0.35 : 1}
            style={{ transition: 'opacity 120ms ease', cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            aria-pressed={selectedStatus === a.status}
            aria-label={`${a.label}: ${a.count}`}
            onMouseEnter={() => onHover(a.status)}
            onMouseLeave={() => onHover(null)}
            onClick={() => onSelect(a.status)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(a.status);
              }
            }}
          >
            <title>{`${a.label}: ${a.count}`}</title>
          </circle>
        ))}
      </g>
      <text x="90" y="86" textAnchor="middle" className="dash-oprog-hero">
        {centerValue}
      </text>
      <text x="90" y="106" textAnchor="middle" className="dash-oprog-sub">
        {centerCaption}
      </text>
    </svg>
  );
}

/* ── Кільце одного замовлення ──────────────────────────────────────────── */

function OrderMeter({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct));
  const dash = (clamped / 100) * C;
  return (
    <svg
      viewBox="0 0 180 180"
      className="dash-oprog-ring"
      role="img"
      aria-label={`Виконано ${clamped}%`}
    >
      <g transform="rotate(-90 90 90)">
        <circle cx="90" cy="90" r={R} fill="none" stroke="var(--brand-bg)" strokeWidth={STROKE} />
        {dash > 0 && (
          <circle
            cx="90"
            cy="90"
            r={R}
            fill="none"
            stroke="var(--brand)"
            strokeWidth={STROKE}
            strokeDasharray={`${dash} ${C - dash}`}
            strokeLinecap={clamped >= 100 ? 'butt' : 'round'}
          />
        )}
      </g>
      <text x="90" y="86" textAnchor="middle" className="dash-oprog-hero">
        {clamped}%
      </text>
      <text x="90" y="106" textAnchor="middle" className="dash-oprog-sub">
        виготовлено
      </text>
    </svg>
  );
}

/* ── Картка одного замовлення: режим вибору і вікно перегляду ──────────── */

function OrderDetail({
  order,
  withPlanningLink = false,
}: {
  order: OrderOverviewRow;
  withPlanningLink?: boolean;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['orders', order.id],
    queryFn: () => ordersApi.get(order.id),
  });

  const dl = deadlineLabel(order.days_to_deadline);

  return (
    <div className="dash-oprog-grid">
      <div>
        <OrderMeter pct={order.progress_pct} />
        <div className="dash-oprog-readout">
          {formatQty(order.produced_quantity)} з {formatQty(order.total_quantity)}
        </div>
      </div>

      <div>
        <div className="dash-oprog-orderhead">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="dash-oprog-ordernum">{orderNumber(order)}</span>
            {order.is_development && <span className="dash-oprog-vdr">ВДР</span>}
            <StatusChip tone={STATUS_TONE[order.status] ?? 'neutral'} size="sm">
              {order.status_display}
            </StatusChip>
          </div>
          <div className="dash-oprog-customer">{order.customer_name}</div>
          <div className={`dash-oprog-deadline ${dl.cls}`}>
            {order.deadline ? `${formatDate(order.deadline)} · ${dl.text}` : 'без дедлайну'}
          </div>
        </div>

        {isLoading ? (
          <div style={{ padding: 20, textAlign: 'center' }}>
            <Spin size="small" />
          </div>
        ) : isError ? (
          <div className="dash-oprog-error">Не вдалося завантажити позиції замовлення.</div>
        ) : (
          <div className="dash-oprog-items">
            <div className="dash-oprog-items-head">
              <span>Виріб</span>
              <span className="r">Потрібно</span>
              <span className="r">Виготовлено</span>
            </div>
            {(data?.items ?? []).map((it) => {
              const need = parseFloat(it.quantity) || 0;
              const done = parseFloat(it.produced_quantity) || 0;
              const full = done >= need && need > 0;
              return (
                <div key={it.id} className={`dash-oprog-item ${full ? 'is-done' : ''}`}>
                  <span className="dash-oprog-item-name" title={it.product_name}>
                    {it.product_name}
                  </span>
                  <span className="r tabnum">{formatQty(it.quantity, it.unit)}</span>
                  <span className="r tabnum">{formatQty(it.produced_quantity, it.unit)}</span>
                </div>
              );
            })}
          </div>
        )}

        {withPlanningLink && (
          <div className="dash-oprog-modallink">
            <Link to="/orders">Відкрити в плануванні →</Link>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Список замовлень праворуч ─────────────────────────────────────────── */

function OrdersList({
  title,
  orders,
  showStatusDot,
  onOpen,
}: {
  title: string;
  orders: OrderOverviewRow[];
  showStatusDot: boolean;
  onOpen: (order: OrderOverviewRow) => void;
}) {
  return (
    <div className="dash-oprog-listcol">
      <div className="dash-oprog-listhead">
        {title} · {orders.length}
      </div>
      {orders.length === 0 ? (
        <div className="dash-oprog-listempty">Немає замовлень у цьому стані</div>
      ) : (
        <div className="dash-oprog-list">
          {orders.map((o) => {
            const dl = deadlineLabel(o.days_to_deadline);
            return (
              <button
                key={o.id}
                type="button"
                className="dash-oprog-olist-row"
                onClick={() => onOpen(o)}
              >
                <span className="dash-oprog-olist-num">
                  {showStatusDot && (
                    <span
                      className="dash-oprog-olist-dot"
                      style={{ background: rampColor(o.status) }}
                      title={o.status_display}
                    />
                  )}
                  {orderNumber(o)}
                  {o.is_development && <span className="dash-oprog-vdr">ВДР</span>}
                </span>
                <span className={`dash-oprog-olist-deadline ${dl.cls}`}>
                  {o.deadline ? formatDate(o.deadline) : 'без дедлайну'}
                </span>
                <span className="dash-oprog-olist-customer">{o.customer_name}</span>
                <span className="dash-oprog-olist-pos">{o.items_count} поз.</span>
                {o.status !== 'new' && (
                  <span className="dash-oprog-olist-prog">
                    <span className="dash-oprog-bar">
                      <span
                        className="dash-oprog-bar-fill"
                        style={{ width: `${Math.max(0, Math.min(100, o.progress_pct))}%` }}
                      />
                    </span>
                    <span className="dash-oprog-olist-pct">{o.progress_pct} %</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Віджет ────────────────────────────────────────────────────────────── */

export function OrdersProgressWidget({ overview }: { overview: OrdersOverview }) {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<number>(ALL);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [modalOrder, setModalOrder] = useState<OrderOverviewRow | null>(null);

  const selected = useMemo(
    () => overview.orders.find((o) => o.id === selectedId) ?? null,
    [overview.orders, selectedId],
  );

  /** Список праворуч: усі активні або лише обраний стан. */
  const listOrders = useMemo(
    () =>
      selectedStatus
        ? ordersInStatus(overview.orders, selectedStatus)
        : sortByDeadline(overview.orders),
    [overview.orders, selectedStatus],
  );

  const statusSlice = selectedStatus
    ? (overview.by_status.find((s) => s.status === selectedStatus) ?? null)
    : null;

  /**
   * «Нові» за визначенням мають 0 % — відсоток у центрі був би порожньою
   * цифрою, тож для них показуємо кількість, як і для всіх замовлень.
   */
  const showPct = selectedStatus !== null && selectedStatus !== 'new';
  const centerValue = showPct
    ? `${averageProgress(listOrders)}%`
    : String(selectedStatus ? listOrders.length : overview.orders_count);
  const centerCaption = showPct ? 'у середньому' : 'замовлень';

  const pickStatus = (status: string) => {
    setSelectedId(ALL);
    setSelectedStatus((prev) => (prev === status ? null : status));
  };

  const pickOrder = (value: number) => {
    setSelectedId(value);
    if (value !== ALL) setSelectedStatus(null);
  };

  const options = [
    { value: ALL, label: `Усі активні замовлення · ${overview.orders_count}` },
    ...overview.orders.map((o) => ({ value: o.id, label: orderLabel(o) })),
  ];

  const hoveredSlice = hovered ? overview.by_status.find((s) => s.status === hovered) : null;

  return (
    <div className="dash-card">
      <div className="dash-card-head">
        <span className="dash-section-label">Виконання замовлень</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Select
            size="small"
            value={selectedId}
            onChange={pickOrder}
            options={options}
            showSearch
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            style={{ minWidth: 260 }}
            disabled={overview.orders_count === 0}
          />
          <span className="dash-link" onClick={() => navigate('/orders')}>
            Планування →
          </span>
        </div>
      </div>

      {overview.orders_count === 0 ? (
        <Empty description="Активних замовлень немає" />
      ) : selected === null ? (
        <div className="dash-oprog-grid3">
          <div>
            <StatusDonut
              overview={overview}
              hovered={hovered}
              selectedStatus={selectedStatus}
              centerValue={centerValue}
              centerCaption={centerCaption}
              onHover={setHovered}
              onSelect={pickStatus}
            />
            <div className="dash-oprog-readout">
              {hoveredSlice
                ? `${hoveredSlice.label} · ${hoveredSlice.count} з ${overview.orders_count}`
                : ''}
            </div>
          </div>

          <div>
            {selectedStatus && (
              <button
                type="button"
                className="dash-oprog-allbtn"
                onClick={() => setSelectedStatus(null)}
              >
                ← Усі
              </button>
            )}
            <div className="dash-oprog-legend">
              {overview.by_status.map((s) => (
                <button
                  key={s.status}
                  type="button"
                  disabled={s.count === 0}
                  aria-pressed={selectedStatus === s.status}
                  className={`dash-oprog-row ${hovered === s.status ? 'is-hover' : ''} ${
                    selectedStatus === s.status ? 'is-selected' : ''
                  } ${s.count === 0 ? 'is-empty' : ''}`}
                  onMouseEnter={() => s.count > 0 && setHovered(s.status)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => pickStatus(s.status)}
                >
                  <span className="dash-oprog-swatch" style={{ background: rampColor(s.status) }} />
                  <span className="dash-oprog-label">{s.label}</span>
                  <span className="dash-oprog-count">{s.count}</span>
                </button>
              ))}
            </div>
            {showPct && (
              <div className="dash-oprog-note">
                Відсоток у центрі — середнє виконання по замовленнях цього стану: виготовлено з
                потрібного, кожне замовлення з однаковою вагою.
              </div>
            )}
          </div>

          <OrdersList
            title={statusSlice ? statusSlice.label : 'Усі активні'}
            orders={listOrders}
            showStatusDot={selectedStatus === null}
            onOpen={setModalOrder}
          />
        </div>
      ) : (
        <OrderDetail order={selected} />
      )}

      <Modal
        open={modalOrder !== null}
        onCancel={() => setModalOrder(null)}
        footer={null}
        width={860}
        destroyOnClose
        title={modalOrder ? `Замовлення ${orderNumber(modalOrder)}` : ''}
      >
        {modalOrder && <OrderDetail order={modalOrder} withPlanningLink />}
      </Modal>
    </div>
  );
}
